import { IconID } from '@common/Icon/Icon';
import { InputChangeConfirmationWrapper } from '@common/InputChangeConfirmationWrapper/InputChangeConfirmationWrapper';
import { TooltipIcon } from '@common/TooltipIcon/TooltipIcon';
import { WidenImage } from '@common/Widen/WidenImage';
import { KetteEinbauArt } from '@hooks/states/echainConfigSlice';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import { useVerfahrwegWarning } from '@hooks/useVerfahrwegWarning';
import { EXTERNAL_LINKS } from '@libs/redirect.helper';
import { ProductHelper } from '@models/graphql';
import { SelectorBox } from '@product/VariantSelector/SelectorBox/SelectorBox';
import { VariantSelector } from '@product/VariantSelector/VariantSelector';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import type { EinbauartProperties } from '../ProductSelectionTab';
import styles from './InstallationTypeSelection.module.scss';

export interface InstallationTypeSelectionProps {
  einbauarten: EinbauartProperties[];
  einbauArt: KetteEinbauArt;
  onSelect: (type: KetteEinbauArt) => void;
  locale?: string;
}

export const InstallationTypeSelection = ({
  locale,
  einbauarten,
  einbauArt,
  onSelect,
}: InstallationTypeSelectionProps) => {
  const { t } = useTranslation(['product/echainBuyBox']);
  const { showVerfahrwegWarning, verfahrwegWarningText } = useVerfahrwegWarning();

  const currentProductId = useProductPageStore(state => state.currentProduct.id);
  const selectedSupportTrayVariant = useEchainProductStore(state => state.selectedSupportTrayVariant);
  const clearSelectedSupportTray = useEchainProductStore(state => state.clearSelectedSupportTray);

  // when the selected Einbauart changes to a different one,
  // we need to unselect the support trays and remove them from the parts list,
  // because the support tray component will not be shown anymore
  const hasToConfirmSelection = (einbauArtSelection: KetteEinbauArt) =>
    Boolean(einbauArtSelection !== einbauArt && selectedSupportTrayVariant && einbauArt === KetteEinbauArt.freitragend);
  return (
    <div className={classNames('px-6', 'py-8')}>
      <h3 className={classNames('mb-8', 'flex', 'font-bold', 'text-sm')}>
        {t('ECHAIN_PRODUCT_PAGE.SECTION_TITLES.INSTALLATION_TYPE_TITLE')}
        {/* protected space with nbsp to prevent line breaks */}
        &nbsp;
        <TooltipIcon
          id={IconID.INFO_CIRCLE_GRAY}
          size={6}
          tooltipContent={
            <div className={classNames('min-w-[250px]', 'font-normal')}>
              <div className={classNames('font-bold')}>{t('ECHAIN_PRODUCT_PAGE.TOOLTIPS.INSTALLATION_TYPE.TITLE')}</div>
              <div className={classNames('mb-4')}>
                {t('ECHAIN_PRODUCT_PAGE.TOOLTIPS.INSTALLATION_TYPE.INTRODUCTION_TEXT')}
              </div>
              <div>
                <ul className={classNames('ml-8', 'list-disc', 'text-igus-primary')}>
                  <li>
                    <a
                      href={EXTERNAL_LINKS.UNSUPPORTED_ECHAIN_INFO(locale)}
                      className={classNames('text-igus-primary')}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('ECHAIN_PRODUCT_PAGE.TOOLTIPS.INSTALLATION_TYPE.UNSUPPORTED_TEXT')}
                    </a>
                  </li>
                  <li>
                    <a
                      href={EXTERNAL_LINKS.GLIDING_ECHAIN_INFO(locale)}
                      className={classNames('text-igus-primary')}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('ECHAIN_PRODUCT_PAGE.TOOLTIPS.INSTALLATION_TYPE.GLIDING_TEXT')}
                    </a>
                  </li>
                  <li>
                    <a
                      href={EXTERNAL_LINKS.HANGING_ECHAIN_INFO(locale)}
                      className={classNames('text-igus-primary')}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('ECHAIN_PRODUCT_PAGE.TOOLTIPS.INSTALLATION_TYPE.HANGING_TEXT')}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          }
          interactive
          iconInline
        />
      </h3>
      <div
        data-testid="installationOptionsSelector"
        className={classNames('grid', 'grid-cols-1', `sm:grid-cols-2`, `lg:grid-cols-3`, 'gap-8', 'mb-8')}
      >
        {einbauarten.map(einbauartProps => {
          if (!einbauartProps.available) {
            return null;
          }

          const image = ProductHelper.convertImageUriToWidenImage(einbauartProps.iconUrl, einbauartProps.title);
          const optionSelectorBox = (
            <SelectorBox
              objectKey={einbauartProps.type}
              selected={einbauArt === einbauartProps.type}
              onSelect={() => onSelect(einbauartProps.type)}
              dataTestId={`selector-box-${einbauartProps.type}`}
              disabled={einbauartProps.disabled}
              showWarning={einbauartProps.type === KetteEinbauArt.gleitend && showVerfahrwegWarning}
              warningText={verfahrwegWarningText}
            >
              <p className="mb-8">
                <strong>{einbauartProps.title}</strong>
              </p>
              <div className={classNames(styles.installationOptionImgWrapper, 'overflow-hidden', 'h-0', 'relative')}>
                <WidenImage
                  shortCode={image.shortCode}
                  title={image.title}
                  filename={image.filename}
                  width={0}
                  height={0}
                  layout="fill"
                />
              </div>
            </SelectorBox>
          );

          return (
            <div data-testid="installationOptionsSelectorBox" key={`installation-type-selector-${einbauartProps.type}`}>
              {einbauArt === KetteEinbauArt.freitragend ? (
                <InputChangeConfirmationWrapper
                  text={t('ECHAIN_PRODUCT_PAGE.USER_CONFIRMATION_MODAL.REMOVE_SUPPORT_TRAY_DESCRIPTION')}
                  hasToConfirm={hasToConfirmSelection(einbauartProps.type)}
                  actionToConfirm={() => clearSelectedSupportTray(currentProductId)}
                >
                  {optionSelectorBox}
                </InputChangeConfirmationWrapper>
              ) : (
                optionSelectorBox
              )}
            </div>
          );
        })}
      </div>
      <h3 className={classNames('mb-8 font-bold text-sm')}>
        {t('ECHAIN_PRODUCT_PAGE.SECTION_TITLES.ENERGYCHAIN_TITLE')}{' '}
      </h3>
      <VariantSelector columnLayout />
    </div>
  );
};
